// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  set,
  get
} from 'firebase/database';
import { app } from '../utils/firebase';

const auth = getAuth(app);
const db = getDatabase(app);

export const AuthContext = createContext();

/** Boolean admin flag for UI; DB may store true, 1, or string "true" (Firebase rules only matched strict true before). */
function normalizeProfile(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const v = raw.isAdmin;
  const isAdmin =
    v === true ||
    v === 1 ||
    v === '1' ||
    (typeof v === 'string' && v.toLowerCase() === 'true');
  return { ...raw, isAdmin: Boolean(isAdmin) };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      
        try {
          const snapshot = await get(ref(db, `profiles/${currentUser.uid}`));
          if (snapshot.exists()) {
            setProfile(normalizeProfile(snapshot.val()));
          } else {
            setProfile(null);
          }
        } catch (err) {
          console.error('Error loading profile:', err);
          setProfile(null);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signup = async (email, password, name) => {
    const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
    const profileData = { name, email, createdAt: Date.now() };
    await set(ref(db, `profiles/${newUser.uid}`), profileData);
    setProfile(profileData);
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signup, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
// export modules
export default AuthContext;